/**
 * Run-scoped training materials panel (NC-1098, Feat 2.0 #1090) — mounted on
 * RunDetailsPage (Übersicht tab). Two concerns for the tutor:
 *  1. Ad-hoc run-scoped uploads (runId overlay) on top of the workload template.
 *  2. "Jetzt auf VMs verteilen" — dispatches runs-materials-sync (active runs,
 *     5-min lock) to re-run the idempotent 140 delivery against the run VMs.
 *
 * The workload TEMPLATE (folder structure) is edited on the WorkloadEditor tab
 * (TrainingFilesTab); this panel only shows the per-run overlay (runId set).
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { runsApi, extractErrorMessage } from '../services/api';
import { trainingFilesApi } from '../services/trainingFilesService';
import { TrainingFileMetadata } from '../services/types';
import { useApiQuery } from '../hooks/useApiQuery';
import { useExpiringLock } from '../hooks/useExpiringLock';
import { formatDateSafe } from '../utils/dateFormat';
import { formatBytes } from '../utils/formatBytes';
import TrainingFileUploadDialog from './workload/TrainingFileUploadDialog';

interface Props {
  runId: string;
  /** Workload key of the run (run.workloadId ?? run.workload) — training-data pk. */
  workloadId: string;
  runStatus?: string;
  /** ISO — set when a sync was last dispatched; drives the 5-min lock display. */
  materialsSyncRequestedUtc?: string;
  materialsSyncJobUrl?: string;
  /** operator+ only — the parent gates rendering, this is a defensive echo. */
  canOperate: boolean;
}

const SYNC_LOCK_MS = 5 * 60 * 1000;

export function RunMaterialsPanel({
  runId,
  workloadId,
  runStatus,
  materialsSyncRequestedUtc,
  materialsSyncJobUrl,
  canOperate,
}: Props) {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [distributeBusy, setDistributeBusy] = useState(false);
  const [distributeMsg, setDistributeMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    data: allFiles,
    loading,
    error: fetchError,
    refetch,
  } = useApiQuery<TrainingFileMetadata[]>(
    () => trainingFilesApi.list(workloadId),
    [workloadId],
    { enabled: !!workloadId && canOperate }
  );

  // Only this run's ad-hoc overlay — the workload template lives on the editor.
  const runFiles = useMemo(
    () => (allFiles ?? []).filter((f) => f.runId === runId && f.kind !== 'folder'),
    [allFiles, runId]
  );

  // Mirror the BE 5-min dispatch lock so the button disables instead of 409-ing.
  // NC-1258: shares useExpiringLock with RunVerificationPanel — this panel had
  // the same frozen-Date.now() memo, so "Jetzt auf VMs verteilen" also stayed
  // disabled past the lock until the page was remounted.
  const syncLockSecondsAgo = useExpiringLock(materialsSyncRequestedUtc, SYNC_LOCK_MS);
  const syncLockActive = syncLockSecondsAgo !== null;
  const isActive = runStatus === 'active';
  const distributeDisabled = distributeBusy || !isActive || syncLockActive;

  const distributeTooltip = !isActive
    ? `Verteilen braucht laufende VMs (Status: ${runStatus ?? 'unbekannt'}).`
    : syncLockActive
      ? `Eine Verteilung läuft bereits (vor ${syncLockSecondsAgo}s gestartet) — bitte warten.`
      : 'Rollt die aktuellen Unterlagen (Vorlage + Lauf-Overlay) auf alle VMs dieses Laufs aus.';

  const triggerDistribute = async () => {
    setDistributeBusy(true);
    setError(null);
    setDistributeMsg(null);
    try {
      const res = await runsApi.materialsSync(runId);
      setDistributeMsg(
        res.jobUrl
          ? 'Verteilung gestartet — die Unterlagen erscheinen in wenigen Minuten auf den VMs.'
          : 'Verteilung gestartet.'
      );
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setDistributeBusy(false);
    }
  };

  if (!canOperate) return null;

  const displayError = error || fetchError;

  return (
    <Card data-testid="run-materials-panel">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Unterlagen (dieser Lauf)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Zusätzlich zur Workload-Vorlage können hier lauf-spezifische Dateien hochgeladen und
          jederzeit auf die laufenden VMs (<code>C:\Schulungsunterlagen</code>) verteilt werden.
        </Typography>

        {displayError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {displayError}
          </Alert>
        )}
        {distributeMsg && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDistributeMsg(null)}>
            {distributeMsg}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => setUploadOpen(true)}
          >
            Datei hochladen
          </Button>
          <Tooltip title={distributeTooltip}>
            <span>
              <Button
                variant="contained"
                startIcon={distributeBusy ? <CircularProgress size={18} /> : <CloudSyncIcon />}
                disabled={distributeDisabled}
                onClick={triggerDistribute}
                data-testid="distribute-btn"
              >
                Jetzt auf VMs verteilen
              </Button>
            </span>
          </Tooltip>
          {materialsSyncRequestedUtc && (
            <Chip
              size="small"
              variant="outlined"
              color={syncLockActive ? 'warning' : 'default'}
              label={`Letzte Verteilung: ${formatDateSafe(materialsSyncRequestedUtc, 'dd.MM.yyyy HH:mm')}`}
            />
          )}
          {materialsSyncJobUrl && (
            <Link href={materialsSyncJobUrl} target="_blank" rel="noopener" variant="body2">
              Job-Log
            </Link>
          )}
        </Stack>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Lauf-spezifische Dateien</Typography>
          {loading ? (
            <Box sx={{ py: 1 }}>
              <CircularProgress size={20} />
            </Box>
          ) : runFiles.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Keine lauf-spezifischen Dateien — es gilt die Workload-Vorlage.
            </Typography>
          ) : (
            <List dense>
              {runFiles.map((f) => (
                <ListItem key={f.id} data-testid={`run-file-${f.id}`}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <InsertDriveFileIcon fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {f.relativePath ? `${f.relativePath}/${f.fileName}` : f.fileName}
                        <Chip
                          size="small"
                          label={f.status === 'validated' ? 'bereit' : 'ausstehend'}
                          color={f.status === 'validated' ? 'success' : 'warning'}
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      </Box>
                    }
                    secondary={`${formatBytes(f.sizeBytes)} · ${formatDateSafe(f.uploadedAt, 'dd.MM.yyyy HH:mm')}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        <TrainingFileUploadDialog
          open={uploadOpen}
          workloadId={workloadId}
          runId={runId}
          title="Datei für diesen Lauf hochladen"
          onClose={() => setUploadOpen(false)}
          onUploaded={refetch}
        />
      </CardContent>
    </Card>
  );
}
